import { create } from 'zustand';
import type { Slide, SlideContent, SlideStyle, CastMessage, VideoAction } from '@proclaim/shared';
import { castManager } from '../lib/cast-manager';
import { useFontStore } from './font-store';
import { api } from '../shared/api';

// ── Image URL helpers ─────────────────────────────────────────────────────────
// Sender displays images from /uploads/images/… (local server, fast LAN).
// Receiver is HTTPS — local HTTP URLs cause mixed-content block.
// Solution: map local paths → R2/CDN cloudUrl (HTTPS) for Cast messages only.

const LOCAL_IMG_PREFIX = '/uploads/images/';
const SERVER_LOCAL = `http://${window.location.hostname}:${window.location.port || 3000}`;

// Populated on Cast session start. Module-level singleton matches store lifecycle.
let _imageCloudMap = new Map<string, string>();

/** Resolve a local /uploads/images/ path to R2 cloudUrl if available. */
export function resolveImageUrl(url: string): string {
  return toReceiverUrl(url);
}

export async function refreshImageCloudMap(): Promise<void> {
  try {
    const images = await api.getImages();
    const map = new Map<string, string>();
    for (const img of images) {
      if (img.cloudUrl) map.set(`${LOCAL_IMG_PREFIX}${img.filename}`, img.cloudUrl);
    }
    _imageCloudMap = map;
    useCastStore.setState((s) => ({ imageCloudMapVersion: s.imageCloudMapVersion + 1 }));
  } catch {
    // non-fatal — stale map stays valid
  }
}

function toReceiverUrl(url: string): string {
  if (!url) return url;
  // Relative local path → resolve via cloudUrl (R2/CDN, HTTPS) for Cast receiver.
  // Cast Shell is Chromium and blocks HTTP subresources on HTTPS pages (mixed content).
  // Fallback: make absolute with sender origin so WS receivers can load it too.
  if (url.startsWith(LOCAL_IMG_PREFIX)) {
    return _imageCloudMap.get(url) ?? (window.location.origin + url);
  }
  // Absolute local HTTP URL (any port: 3000 API or 5173 Vite dev) → strip to relative key
  for (const prefix of [SERVER_LOCAL, window.location.origin]) {
    if (url.startsWith(prefix)) {
      const rel = url.slice(prefix.length);
      return _imageCloudMap.get(rel) ?? url;
    }
  }
  // HTTPS (external CDN, user-typed) → pass through
  return url;
}

function rewriteForReceiver(message: CastMessage): CastMessage {
  if (message.type === 'SHOW_SLIDE' && message.slide) {
    const slide = message.slide;
    const newStyle = slide.style?.bgImage
      ? { ...slide.style, bgImage: toReceiverUrl(slide.style.bgImage) }
      : slide.style;
    const newElements = slide.elements?.map((el) =>
      el.type === 'image' ? { ...el, src: toReceiverUrl((el as { src: string }).src) } : el,
    );
    return {
      ...message,
      slide: {
        ...slide,
        style: newStyle,
        ...(newElements ? { elements: newElements } : {}),
      },
    };
  }
  if (message.type === 'PRELOAD_IMAGES' && message.imageUrls) {
    return { ...message, imageUrls: message.imageUrls.map(toReceiverUrl) };
  }
  return message;
}

function slideImageUrls(slide: Slide): string[] {
  const urls: string[] = [];
  if (slide.style?.bgImage) urls.push(slide.style.bgImage);
  for (const el of slide.content?.elements ?? []) {
    if (el.type === 'image' && (el as { src: string }).src) urls.push((el as { src: string }).src);
  }
  return urls;
}

function preloadImages(slides: Slide[]) {
  for (const slide of slides) {
    for (const url of slideImageUrls(slide)) {
      const img = new Image(); img.src = url;
    }
  }
}

const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = `${WS_PROTOCOL}//${window.location.host}/cast`;

/**
 * CAST_APP_ID: replace with your App ID from cast.google.com/publish
 * Use chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID for quick tests
 * (but it does NOT support custom namespaces/messages).
 * Example custom app ID: 'ABCD1234'
 */
const CAST_APP_ID = import.meta.env.VITE_CAST_APP_ID as string | undefined;

// ── Heartbeat ────────────────────────────────────────────────────────────────
// PING cada 4 s (Web Worker) mientras hay sesión Cast activa.
// El intervalo corto (4 s < maxInactivity 10 s) minimiza la ventana en que el
// Chromecast puede activar Ambient Mode si la sesión cae brevemente.
let heartbeatWorker: Worker | null = null;
let heartbeatBlobUrl: string | null = null;
let heartbeatOnVisible: (() => void) | null = null;

// Slide ID received from another sender before local slides were loaded.
// Applied on next setSlides/syncSlides call to achieve initial position sync.
let _pendingSyncSlideId: string | null = null;

// Guard against DB rows with duplicate slide IDs — React rejects duplicate
// keys and renders the first instance only. Dedup at every ingress point.
function dedupSlides<T extends { id: string }>(slides: T[]): T[] {
  const seen = new Set<string>();
  return slides.filter((s) => { if (seen.has(s.id)) return false; seen.add(s.id); return true; });
}

// ── Auto-rejoin ───────────────────────────────────────────────────────────────
// Cuando la sesión cae, re-llama setOptions() al recuperar visibilidad para que
// el SDK compruebe si el receiver sigue activo y reconecte sin gesto del usuario.
let rejoinOnVisible: (() => void) | null = null;
let rejoinTimer: ReturnType<typeof setInterval> | null = null;

function startAutoRejoin() {
  stopAutoRejoin();
  const attempt = () => {
    if (useCastStore.getState().castActiveDevices.length === 0) castManager.rejoin();
  };
  rejoinOnVisible = () => { if (document.visibilityState === 'visible') attempt(); };
  document.addEventListener('visibilitychange', rejoinOnVisible);
  // También intenta cada 10 s por si el tab ya estaba visible cuando cayó la sesión
  rejoinTimer = setInterval(attempt, 10_000);
}

function stopAutoRejoin() {
  if (rejoinOnVisible) {
    document.removeEventListener('visibilitychange', rejoinOnVisible);
    rejoinOnVisible = null;
  }
  if (rejoinTimer !== null) {
    clearInterval(rejoinTimer);
    rejoinTimer = null;
  }
}

function startHeartbeat(send: () => void) {
  stopHeartbeat();

  const code = `
    let t = null;
    onmessage = function(e) {
      if (e.data === 'start') {
        if (t) clearInterval(t);
        t = setInterval(function() { postMessage('tick'); }, 4000);
      } else {
        clearInterval(t);
        t = null;
      }
    };
  `;
  const blobUrl = URL.createObjectURL(new Blob([code], { type: 'application/javascript' }));
  const w = new Worker(blobUrl);
  w.onmessage = () => send();
  w.postMessage('start');
  heartbeatWorker = w;
  heartbeatBlobUrl = blobUrl;

  // When the tab regains visibility, send an immediate PING so the Cast channel
  // stays alive even if it missed beats while in background.
  heartbeatOnVisible = () => { if (document.visibilityState === 'visible') send(); };
  document.addEventListener('visibilitychange', heartbeatOnVisible);
}

function stopHeartbeat() {
  if (heartbeatWorker) {
    heartbeatWorker.terminate();
    heartbeatWorker = null;
  }
  if (heartbeatBlobUrl) {
    URL.revokeObjectURL(heartbeatBlobUrl);
    heartbeatBlobUrl = null;
  }
  if (heartbeatOnVisible) {
    document.removeEventListener('visibilitychange', heartbeatOnVisible);
    heartbeatOnVisible = null;
  }
}

if (!CAST_APP_ID) {
  console.warn('[Cast] VITE_CAST_APP_ID no está definido en .env — Cast SDK desactivado');
}

// ── Session persistence (survives page refresh) ──────────────────────────────
const SESSION_KEY = 'proclaim-live';

function saveSession(slides: Slide[], index: number, black: boolean) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ slides, index, black }));
  } catch { /* storage quota — non-fatal */ }
}

function loadSession(): { slides: Slide[]; index: number; black: boolean } | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { slides: Slide[]; index: number; black: boolean };
    // Restored sessions may contain duplicate slide IDs if the source data was
    // bad when stored. Dedup so React doesn't warn on hydration.
    if (Array.isArray(parsed.slides)) parsed.slides = dedupSlides(parsed.slides);
    return parsed;
  } catch { return null; }
}

const _session = loadSession();

const QUICK_SLIDE_KEY = 'proclaim-quick-slide';

function saveQuickSession(qs: { content: SlideContent; style: SlideStyle } | null) {
  try {
    if (qs) sessionStorage.setItem(QUICK_SLIDE_KEY, JSON.stringify(qs));
    else sessionStorage.removeItem(QUICK_SLIDE_KEY);
  } catch { /* non-fatal */ }
}

function loadQuickSession(): { content: SlideContent; style: SlideStyle } | null {
  try {
    const raw = sessionStorage.getItem(QUICK_SLIDE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export type CastTransport = 'ws' | 'chromecast';

interface CastState {
  // Connection state
  transport: CastTransport;
  connected: boolean;
  castSdkReady: boolean;
  castAvailable: boolean;
  castActiveDevices: string[];      // names of connected Chromecast devices
  castConnectingDevices: string[];  // names of devices currently being connected
  castDiscoveredDevices: ElectronChromecastDevice[]; // all discovered devices (Electron)
  castSyncEnabled: boolean; // add presentAt to multi-cast messages for simultaneous switch

  // Slides state
  currentSlideIndex: number;
  slides: Slide[];
  blackScreen: boolean;

  // WS role tracking — server counts separately after HELLO handshake
  wsReceiverCount: number;     // display screens (role: receiver)
  wsSenderCount: number;       // other controllers (role: sender, excluding self)
  wsActiveCastDevices: string[];    // Chromecast device names received via SESSION_INFO
  wsDiscoveredDevices: string[];    // Available Chromecast device names received via DEVICES_INFO

  // Quick verse (active out-of-deck slide cast)
  quickSlide: { content: SlideContent; style: SlideStyle } | null;

  // Image cloud map version — bumped each time refreshImageCloudMap() completes.
  // ReceiverPreview subscribes so it re-sends the slide when R2 URLs become available.
  imageCloudMapVersion: number;

  // Last VIDEO_CONTROL message received from the server (originated by another
  // sender or by the master-clock cold-join). LiveController subscribes so its
  // hidden local-audio iframe can stay in sync with the receivers.
  // nonce increments per message so identical actions still trigger a re-apply.
  // serverNow / echoClientNow allow the consumer to compute RTT compensation.
  lastRemoteVideoControl: {
    action: VideoAction;
    seekTime?: number;
    serverNow?: number;
    echoClientNow?: number;
    presentAt?: number;
    nonce: number;
  } | null;

  // Periodic drift-correction signal from server (VIDEO_TICK). Drives local-audio
  // playback alignment with the receivers.
  lastRemoteVideoTick: {
    expectedSeek: number;
    serverNow: number;
    nonce: number;
  } | null;

  // Remote logs shipped by the receiver(s) — errors + lifecycle. Capped ring.
  // Fed by the WS path (server relay) and the Electron IPC path (cast upstream).
  receiverLogs: ReceiverLogEntry[];

  // Internal
  ws: WebSocket | null;

  // Actions
  init: () => void;
  addReceiverLog: (log: ReceiverLogEntry) => void;
  syncCastSessions: () => void;  // reconcile store with main-process actual sessions on mount
  initCastSdk: () => void;
  requestCastSession: (deviceName?: string) => Promise<void>;
  connectAllDevices: () => Promise<void>;
  stopCastDevice: (deviceName: string) => Promise<void>;
  endCastSession: () => void;
  stopCastViaWs: (deviceName?: string) => void;
  startCastViaWs: (deviceName?: string) => void;
  setCastSyncEnabled: (enabled: boolean) => void;
  setSlides: (slides: Slide[]) => void;
  syncSlides: (slides: Slide[], blackOnEntry?: boolean) => void;
  goToSlide: (index: number) => void;
  nextSlide: () => void;
  prevSlide: () => void;
  toggleBlackScreen: () => void;
  sendToReceiver: (message: CastMessage) => void;
  /** Returns the presentAt timestamp stamped on the message, or undefined when single-receiver. */
  sendVideoControl: (action: VideoAction) => number | undefined;
  /** Ask the server for the current video playhead (for local-audio mid-session join). */
  requestVideoSync: () => void;
  setQuickSlide: (qs: { content: SlideContent; style: SlideStyle }) => void;
  clearQuickSlide: () => void;
}

const SYNC_KEY = 'proclaim-cast-sync';

export const useCastStore = create<CastState>((set, get) => ({
  transport: 'ws',
  connected: false,
  castSdkReady: false,
  castAvailable: false,
  castActiveDevices: [],
  castConnectingDevices: [],
  castDiscoveredDevices: [],
  castSyncEnabled: localStorage.getItem(SYNC_KEY) !== 'false', // default on
  currentSlideIndex: _session?.index ?? 0,
  slides: _session?.slides ?? [],
  blackScreen: _session?.black ?? false,
  quickSlide: loadQuickSession(),
  wsReceiverCount: 0,
  wsSenderCount: 0,
  wsActiveCastDevices: [],
  wsDiscoveredDevices: [],
  imageCloudMapVersion: 0,
  lastRemoteVideoControl: null,
  lastRemoteVideoTick: null,
  receiverLogs: [],
  ws: null,

  // Append a receiver log, dedup by (device, seq, ts) since the receiver
  // re-flushes its ring on every reconnect. Capped at 300.
  addReceiverLog: (log) => set((s) => {
    const key = `${log.deviceName ?? ''}|${log.logSeq}|${log.logTs}`;
    if (s.receiverLogs.some((l) => `${l.deviceName ?? ''}|${l.logSeq}|${l.logTs}` === key)) return s;
    const next = [...s.receiverLogs, log];
    if (next.length > 300) next.splice(0, next.length - 300);
    return { receiverLogs: next };
  }),

  // ── WebSocket init (fallback transport) ──────────────────────────────────
  init: () => {
    const { ws: existing } = get();
    if (existing) {
      if (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING) return;
      existing.close();
    }

    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      set({ connected: true });
      // Fetch auth token async — send in HELLO so init() stays synchronous
      // and initCastSdk() is not delayed. Fails silently in dev/no-auth mode.
      api.getCastToken()
        .then((token) => ws.send(JSON.stringify({ type: 'HELLO', role: 'sender', token })))
        .catch(() => ws.send(JSON.stringify({ type: 'HELLO', role: 'sender' })));
      // Announce active and discovered Chromecast devices to already-connected clients
      const { castActiveDevices, castDiscoveredDevices } = get();
      if (castActiveDevices.length > 0) {
        ws.send(JSON.stringify({ type: 'SESSION_INFO', castDevices: castActiveDevices }));
      }
      if (castDiscoveredDevices.length > 0) {
        ws.send(JSON.stringify({ type: 'DEVICES_INFO', devices: castDiscoveredDevices.map((d) => d.name) }));
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as CastMessage;

        if (msg.type === 'SESSION_INFO' && Array.isArray(msg.castDevices)) {
          set({ wsActiveCastDevices: msg.castDevices });
          return;
        }

        if (msg.type === 'DEVICES_INFO' && Array.isArray(msg.devices)) {
          set({ wsDiscoveredDevices: msg.devices });
          return;
        }

        if (msg.type === 'START_CAST') {
          get().requestCastSession(msg.deviceName ?? undefined);
          return;
        }

        // Remote receiver log (WS-mode receivers relay through the server).
        if (msg.type === 'RECEIVER_LOG') {
          get().addReceiverLog({
            logLevel: msg.logLevel,
            logMessage: msg.logMessage,
            logTs: msg.logTs,
            logSource: msg.logSource,
            logSeq: msg.logSeq,
          });
          return;
        }

        // VIDEO_CONTROL from server (other sender's command or master-clock cold-join
        // response). LiveController watches lastRemoteVideoControl to keep its
        // hidden local-audio iframe aligned with the receivers.
        if (msg.type === 'VIDEO_CONTROL' && msg.videoAction) {
          set((s) => ({
            lastRemoteVideoControl: {
              action: msg.videoAction!,
              seekTime: msg.seekTime,
              serverNow: msg.serverNow,
              echoClientNow: msg.echoClientNow,
              presentAt: msg.presentAt,
              nonce: (s.lastRemoteVideoControl?.nonce ?? 0) + 1,
            },
          }));
          return;
        }

        if (msg.type === 'VIDEO_TICK'
          && typeof msg.expectedSeek === 'number'
          && typeof msg.serverNow === 'number') {
          set((s) => ({
            lastRemoteVideoTick: {
              expectedSeek: msg.expectedSeek!,
              serverNow: msg.serverNow!,
              nonce: (s.lastRemoteVideoTick?.nonce ?? 0) + 1,
            },
          }));
          return;
        }

        if (msg.type === 'RECEIVER_COUNT') {
          if (typeof msg.receiverCount === 'number') {
            // New protocol: server tracks roles separately
            set({
              wsReceiverCount: msg.receiverCount,
              wsSenderCount: Math.max(0, (msg.senderCount ?? 1) - 1), // exclude self
            });
          } else if (typeof msg.count === 'number') {
            // Legacy fallback
            set({ wsReceiverCount: Math.max(0, msg.count - 1), wsSenderCount: 0 });
          }
          return;
        }

        // Quick verse from another sender — show banner, no deck index change.
        if (msg.type === 'SHOW_SLIDE' && msg.isQuickSlide && msg.slide) {
          const { style, ...content } = msg.slide;
          set({ quickSlide: { content: content as SlideContent, style } });
          saveQuickSession({ content: content as SlideContent, style });
          // Bridge to Chromecast — msg already has slide payload
          if (get().transport === 'chromecast') {
            get().sendToReceiver({ type: 'SHOW_SLIDE', isQuickSlide: true, slide: msg.slide });
          }
          return;
        }

        // Sync slide position from messages sent by other senders (secondary controllers).
        // When this desktop holds the Cast session it also bridges the message to
        // Chromecast via Cast SDK — the receiver runs Cast SDK only (no WS), so it
        // would never see WS messages without this bridge. No echo loop: server
        // excludes the sender from broadcast, so desktop never receives its own relay.
        if (msg.type === 'SHOW_SLIDE' && msg.slideId) {
          const { slides } = get();

          // Cold join: mobile enters /live with no slides loaded but server replays
          // the last SHOW_SLIDE. Fetch the presentation so the deck becomes visible.
          if (slides.length === 0 && msg.presentationId) {
            api.getSlides(msg.presentationId).then((newSlides) => {
              if (!Array.isArray(newSlides) || newSlides.length === 0) return;
              const typedSlides = dedupSlides(newSlides as Slide[]);
              const syncIdx = typedSlides.findIndex((s) => s.id === msg.slideId);
              const resolvedIdx = syncIdx !== -1 ? syncIdx : 0;
              set({ slides: typedSlides, currentSlideIndex: resolvedIdx, blackScreen: false });
              saveSession(typedSlides, resolvedIdx, false);
              preloadImages(typedSlides);
              _pendingSyncSlideId = null;
            }).catch(() => {/* non-fatal */});
            return;
          }

          const idx = slides.findIndex((s) => s.id === msg.slideId);
          const samePresentation = !!msg.presentationId && slides[0]?.presentationId === msg.presentationId;
          const deckChanged = samePresentation && (
            idx === -1 ||
            (msg.slideCount !== undefined && msg.slideCount !== slides.length)
          );

          if (deckChanged) {
            // Deck changed (slides added/removed/reordered) — silent re-fetch
            api.getSlides(msg.presentationId!).then((newSlides) => {
              if (!Array.isArray(newSlides) || newSlides.length === 0) return;
              const typedSlides = dedupSlides(newSlides as Slide[]);
              const syncIdx = typedSlides.findIndex((s) => s.id === msg.slideId);
              const fallbackIdx = Math.min(get().currentSlideIndex, typedSlides.length - 1);
              const resolvedIdx = syncIdx !== -1 ? syncIdx : fallbackIdx;
              set({ slides: typedSlides, currentSlideIndex: resolvedIdx, blackScreen: false });
              saveSession(typedSlides, resolvedIdx, false);
              preloadImages(typedSlides);
              _pendingSyncSlideId = null;
              // Bridge deck-changed slide to Chromecast
              if (get().transport === 'chromecast') {
                const s = typedSlides[resolvedIdx];
                if (s) get().sendToReceiver({
                  type: 'SHOW_SLIDE',
                  slideId: s.id,
                  presentationId: typedSlides[0]?.presentationId,
                  slideCount: typedSlides.length,
                  slide: { ...s.content, style: s.style },
                });
              }
            }).catch(() => {/* non-fatal — keep stale state */});
            return;
          }

          if (idx !== -1) {
            set({ currentSlideIndex: idx, blackScreen: false });
            saveSession(slides, idx, false);
            _pendingSyncSlideId = null;
            // Bridge to Chromecast — desktop is the only node with a Cast session
            if (get().transport === 'chromecast') {
              const s = slides[idx];
              get().sendToReceiver({
                type: 'SHOW_SLIDE',
                slideId: s.id,
                presentationId: slides[0]?.presentationId,
                slideCount: slides.length,
                slide: { ...s.content, style: s.style },
              });
            }
          } else {
            // Slides not loaded yet — store for when setSlides/syncSlides runs
            _pendingSyncSlideId = msg.slideId;
          }
        }

        if (msg.type === 'BLACK_SCREEN') {
          const { slides, currentSlideIndex } = get();
          set({ blackScreen: true });
          saveSession(slides, currentSlideIndex, true);
          // Bridge to Chromecast
          if (get().transport === 'chromecast') {
            get().sendToReceiver({ type: 'BLACK_SCREEN' });
          }
        }

        if (msg.type === 'STOP_CAST') {
          if (msg.deviceName) {
            get().stopCastDevice(msg.deviceName);
          } else {
            get().endCastSession();
          }
        }
      } catch { /* ignore malformed */ }
    };

    ws.onclose = () => {
      set({ connected: false, ws: null, wsReceiverCount: 0, wsSenderCount: 0 });
      setTimeout(() => get().init(), 3000);
    };

    ws.onerror = () => {
      set({ connected: false });
    };

    set({ ws });

    // Electron: init always (castv2 works without registered App ID for discovery)
    // Browser: only if CAST_APP_ID is configured
    const inElectron = typeof window !== 'undefined' && !!window.electronAPI;
    const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

    // Pre-fetch fonts + image cloud map so they're ready before Cast session starts.
    // Skip in VPS web mode — token not ready at init, would 401. useSyncRefresh
    // handles fetchFonts after SSE connects; refreshImageCloudMap runs at Cast start.
    if (!clerkEnabled || inElectron) {
      useFontStore.getState().fetchFonts().catch(() => {/* non-fatal */});
      refreshImageCloudMap().catch(() => {/* non-fatal */});
    }

    if (CAST_APP_ID || inElectron) get().initCastSdk();
  },

  // ── Sync on mount ─────────────────────────────────────────────────────────
  // Reconciles store state with main-process actual sessions.
  // Fixes state drift when the user navigates away and back: sessions may have
  // dropped (TCP heartbeat timeout) or — less likely — persisted while the store
  // lost track. Call this every time LiveController mounts.
  syncCastSessions: () => {
    if (typeof window === 'undefined' || !window.electronAPI) return;

    window.electronAPI.cast.getActiveSessions().then((liveSessions) => {
      const { castActiveDevices } = get();

      // Derive the reconciled list: intersection + any live sessions store missed
      const reconciled = [
        ...new Set([
          ...castActiveDevices.filter((n) => liveSessions.includes(n)),
          ...liveSessions.filter((n) => !castActiveDevices.includes(n)),
        ]),
      ];

      const changed =
        reconciled.length !== castActiveDevices.length ||
        reconciled.some((n) => !castActiveDevices.includes(n));

      if (!changed) return;

      const nowEmpty = reconciled.length === 0;
      set({
        castActiveDevices: reconciled,
        castConnectingDevices: [],
        ...(nowEmpty ? { transport: 'ws' } : { transport: 'chromecast' }),
      });

      if (nowEmpty) {
        stopHeartbeat();
      } else if (!heartbeatWorker) {
        // Sessions exist but heartbeat was lost — restart it
        startHeartbeat(() => get().sendToReceiver({ type: 'PING' }));
      }
    }).catch(() => {/* non-fatal — keep existing state */});
  },

  // ── Cast SDK init ────────────────────────────────────────────────────────
  initCastSdk: () => {
    castManager.init(CAST_APP_ID!);

    castManager.on((event) => {
      switch (event.type) {
        case 'sdk_ready':
          set({ castSdkReady: true });
          break;

        case 'cast_state_changed': {
          const available = event.state !== 'NO_DEVICES_AVAILABLE';
          set({ castAvailable: available });
          break;
        }

        case 'devices_changed':
          set({ castDiscoveredDevices: event.devices });
          break;

        case 'receiver_log':
          get().addReceiverLog(event.log);
          break;

        case 'session_started': {
          stopAutoRejoin();
          const wasEmpty = get().castActiveDevices.length === 0;
          set((s) => ({
            castActiveDevices: s.castActiveDevices.includes(event.deviceName)
              ? s.castActiveDevices
              : [...s.castActiveDevices, event.deviceName],
            castConnectingDevices: s.castConnectingDevices.filter((n) => n !== event.deviceName),
            transport: 'chromecast',
          }));
          if (wasEmpty) {
            startHeartbeat(() => get().sendToReceiver({ type: 'PING' }));
          }
          useFontStore.getState().fetchFonts().catch(() => {/* non-fatal */});
          refreshImageCloudMap()
            .catch(() => {/* non-fatal */})
            .then(() => {
              const { slides, currentSlideIndex, blackScreen } = get();
              if (blackScreen) {
                get().sendToReceiver({ type: 'BLACK_SCREEN' });
              } else if (slides[currentSlideIndex]) {
                const s = slides[currentSlideIndex];
                const imageUrls = slides
                  .flatMap(slideImageUrls)
                  .filter((u, i, a) => Boolean(u) && a.indexOf(u) === i);
                if (imageUrls.length > 0) {
                  get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls });
                }
                get().sendToReceiver({
                  type: 'SHOW_SLIDE',
                  slide: { ...s.content, style: s.style },
                });
              }
            });
          break;
        }

        case 'session_ended': {
          // deviceName present = Electron per-device; absent = browser clears all
          const newActive = event.deviceName
            ? get().castActiveDevices.filter((d) => d !== event.deviceName)
            : [];
          const nowEmpty = newActive.length === 0;
          set({
            castActiveDevices: newActive,
            castConnectingDevices: nowEmpty ? [] : get().castConnectingDevices,
            ...(nowEmpty ? { transport: 'ws' } : {}),
          });
          if (nowEmpty) {
            stopHeartbeat();
            startAutoRejoin();
          }
          break;
        }

        case 'error':
          console.warn('[Cast]', event.message);
          break;
      }
    });
  },

  // ── Cast session controls ────────────────────────────────────────────────
  requestCastSession: async (deviceName?: string) => {
    if (deviceName) {
      set((s) => ({
        castConnectingDevices: s.castConnectingDevices.includes(deviceName)
          ? s.castConnectingDevices
          : [...s.castConnectingDevices, deviceName],
      }));
    }
    try {
      await castManager.requestSession(deviceName);
    } catch (e) {
      console.warn('[Cast] requestSession cancelled or failed', e);
      if (deviceName) {
        set((s) => ({
          castConnectingDevices: s.castConnectingDevices.filter((n) => n !== deviceName),
        }));
      }
    }
  },

  connectAllDevices: async () => {
    const { castDiscoveredDevices, castActiveDevices, castConnectingDevices, requestCastSession } = get();
    const toConnect = castDiscoveredDevices.filter(
      (d) => !castActiveDevices.includes(d.name) && !castConnectingDevices.includes(d.name),
    );
    await Promise.allSettled(toConnect.map((d) => requestCastSession(d.name)));
  },

  stopCastDevice: async (deviceName: string) => {
    try {
      await castManager.stopDevice(deviceName);
    } catch (e) {
      console.warn('[Cast] stopDevice failed', e);
    }
  },

  endCastSession: () => {
    castManager.endSession();
  },

  stopCastViaWs: (deviceName) => {
    const { ws } = get();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'STOP_CAST', ...(deviceName ? { deviceName } : {}) }));
  },

  startCastViaWs: (deviceName) => {
    const { ws } = get();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'START_CAST', deviceName }));
  },

  setCastSyncEnabled: (enabled) => {
    localStorage.setItem(SYNC_KEY, String(enabled));
    set({ castSyncEnabled: enabled });
  },

  // ── Slide actions ────────────────────────────────────────────────────────
  syncSlides: (slides, blackOnEntry = false) => {
    slides = dedupSlides(slides);
    // Preserve position: find the active slide by ID in the new array
    const { currentSlideIndex, slides: prev } = get();
    const currentId = prev[currentSlideIndex]?.id;
    let newIndex = currentId
      ? Math.max(0, slides.findIndex((s) => s.id === currentId))
      : 0;
    // Override with pending sync from another sender if it resolves in this deck
    if (_pendingSyncSlideId) {
      const syncIdx = slides.findIndex((s) => s.id === _pendingSyncSlideId);
      if (syncIdx !== -1) { newIndex = syncIdx; _pendingSyncSlideId = null; }
    }
    set({ slides, currentSlideIndex: newIndex, blackScreen: blackOnEntry });
    saveSession(slides, newIndex, blackOnEntry);
    preloadImages(slides);

    const imageUrls = slides
      .flatMap(slideImageUrls)
      .filter((url, i, a) => Boolean(url) && a.indexOf(url) === i);

    if (imageUrls.length > 0) {
      get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls });
    }

    // Broadcast the deck reference immediately so the WS server caches it for
    // cold-joining LAN senders, even before refreshImageCloudMap resolves and
    // even when blackOnEntry hides the slide from the receiver.
    const initial = slides[newIndex];
    if (initial) {
      get().sendToReceiver({
        type: 'SHOW_SLIDE',
        slideId: initial.id,
        presentationId: slides[0]?.presentationId,
        slideCount: slides.length,
        slide: { ...initial.content, style: initial.style },
      });
    }

    refreshImageCloudMap()
      .catch(() => {/* non-fatal */})
      .then(() => {
        if (get().slides !== slides) return;
        if (imageUrls.length > 0) {
          get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls });
        }
        if (blackOnEntry) {
          get().sendToReceiver({ type: 'BLACK_SCREEN' });
        } else {
          const s = slides[newIndex];
          if (s) get().sendToReceiver({
            type: 'SHOW_SLIDE',
            slideId: s.id,
            presentationId: slides[0]?.presentationId,
            slideCount: slides.length,
            slide: { ...s.content, style: s.style },
          });
        }
      });
  },

  setSlides: (slides) => {
    slides = dedupSlides(slides);
    // Apply pending sync from another sender if their slideId resolves in this deck
    let startIndex = 0;
    if (_pendingSyncSlideId) {
      const syncIdx = slides.findIndex((s) => s.id === _pendingSyncSlideId);
      if (syncIdx !== -1) { startIndex = syncIdx; _pendingSyncSlideId = null; }
    }
    set({ slides, currentSlideIndex: startIndex, blackScreen: false });
    saveSession(slides, startIndex, false);
    preloadImages(slides); // sender browser preload (for ReceiverPreview in web UI)

    const imageUrls = slides
      .flatMap(slideImageUrls)
      .filter((url, i, a) => Boolean(url) && a.indexOf(url) === i);

    // Phase 1: fire PRELOAD_IMAGES immediately with current map so the receiver
    // starts fetching images that are already known — no need to wait for the
    // API round-trip. Images not yet in the map get skipped here; Phase 2 covers them.
    if (imageUrls.length > 0) {
      get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls });
    }

    // Phase 2: refresh map (catches newly uploaded images), then send the
    // authoritative SHOW_SLIDE and a second PRELOAD_IMAGES pass for any URLs
    // whose cloudUrl just became available. Guard: if setSlides was called again
    // before this resolves, get().slides !== slides — bail to avoid sending stale content.
    refreshImageCloudMap()
      .catch(() => {/* non-fatal */})
      .then(() => {
        if (get().slides !== slides) return; // stale — a newer setSlides call won
        if (imageUrls.length > 0) {
          get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls });
        }
        const idx = get().currentSlideIndex;
        const s = slides[idx];
        if (s) {
          get().sendToReceiver({
            type: 'SHOW_SLIDE',
            slideId: s.id,
            presentationId: slides[0]?.presentationId,
            slideCount: slides.length,
            slide: { ...s.content, style: s.style },
          });
        }
      });
  },

  goToSlide: (index) => {
    const { slides } = get();
    if (index < 0 || index >= slides.length) return;

    // Capa 4: preload adjacent slides on navigation — sender browser
    const neighborUrls: string[] = [];
    for (const offset of [1, 2, -1, -2]) {
      const next = slides[index + offset];
      if (!next) continue;
      for (const url of slideImageUrls(next)) {
        const img = new Image();
        img.src = url;
        neighborUrls.push(url);
      }
    }

    set({ currentSlideIndex: index, blackScreen: false, quickSlide: null });
    saveSession(slides, index, false);
    saveQuickSession(null);

    // Tell receiver to also preload neighbors so its bgImage cache is warm
    // before we ask it to switch. Critical for Chromecast where cache misses
    // cost a network RTT and produce a visible flash.
    if (neighborUrls.length > 0) {
      get().sendToReceiver({ type: 'PRELOAD_IMAGES', imageUrls: neighborUrls });
    }

    get().sendToReceiver({
      type: 'SHOW_SLIDE',
      slideId: slides[index].id,
      presentationId: slides[0]?.presentationId,
      slideCount: slides.length,
      slide: { ...slides[index].content, style: slides[index].style },
    });
  },

  nextSlide: () => {
    const { currentSlideIndex, slides } = get();
    if (currentSlideIndex < slides.length - 1) {
      get().goToSlide(currentSlideIndex + 1);
    }
  },

  prevSlide: () => {
    const { currentSlideIndex } = get();
    if (currentSlideIndex > 0) {
      get().goToSlide(currentSlideIndex - 1);
    }
  },

  toggleBlackScreen: () => {
    const { blackScreen, currentSlideIndex, slides } = get();
    if (blackScreen) {
      get().goToSlide(currentSlideIndex);
    } else {
      set({ blackScreen: true });
      saveSession(slides, currentSlideIndex, true);
      get().sendToReceiver({ type: 'BLACK_SCREEN' });
    }
  },

  // ── Transport-aware send ─────────────────────────────────────────────────
  sendToReceiver: (message) => {
    const { transport, ws, castSyncEnabled, castActiveDevices } = get();

    // Build two variants. Cast Shell loads the receiver over HTTPS and blocks
    // any HTTP subresource (mixed content) — including @font-face URLs. WS
    // receivers run on plain HTTP from the same dev server, so the local fallback
    // is fine there. Only SHOW_SLIDE and BLACK_SCREEN carry font data.
    let wsMessage = message;
    let castMessage = message;

    if (message.type === 'SHOW_SLIDE' || message.type === 'BLACK_SCREEN') {
      const { customFonts } = useFontStore.getState();
      if (customFonts.length > 0) {
        const localBase = `${window.location.protocol}//${window.location.hostname}:3000`;
        const buildFonts = (allowHttp: boolean) => {
          const fontFamilyMap: Record<string, string> = {};
          const rules: string[] = [];
          for (const f of customFonts) {
            fontFamilyMap[f.id] = `'${f.cssFamily}', sans-serif`;
            for (const ff of f.files) {
              let src: string | null = ff.publicUrl ?? null;
              if (!src && allowHttp) src = `${localBase}/uploads/fonts/${ff.filename}`;
              if (!src) continue; // skip — no HTTPS source for Cast
              rules.push(
                `@font-face { font-family: '${f.cssFamily}'; src: url('${src}'); font-weight: ${ff.variable ? '100 900' : ff.weight}; font-style: ${ff.style}; font-display: swap; }`,
              );
            }
          }
          return rules.length > 0
            ? { fontFaceCSS: rules.join('\n'), fontFamilyMap }
            : null;
        };
        const wsFonts = buildFonts(true);
        const castFonts = buildFonts(false);
        if (wsFonts) wsMessage = { ...message, ...wsFonts };
        if (castFonts) castMessage = { ...message, ...castFonts };
      }
    }

    // Rewrite local image paths to R2/CDN cloudUrl for HTTPS receiver
    wsMessage = rewriteForReceiver(wsMessage);
    castMessage = rewriteForReceiver(castMessage);

    // Multi-screen sync: stamp presentAt so all Chromecasts switch simultaneously.
    // Only when sync is enabled, transport is chromecast, and ≥2 sessions active.
    const SYNC_AHEAD_MS = 150;
    const needsSync = castSyncEnabled
      && transport === 'chromecast'
      && castActiveDevices.length > 1
      && (castMessage.type === 'SHOW_SLIDE' || castMessage.type === 'BLACK_SCREEN');
    if (needsSync) {
      castMessage = { ...castMessage, presentAt: Date.now() + SYNC_AHEAD_MS };
    }

    if (transport === 'chromecast') {
      castManager.sendMessage(castMessage).catch((e) => {
        console.warn('[Cast] sendMessage falló — reintentando en 400ms', e);
        setTimeout(() => {
          castManager.sendMessage(castMessage).catch((e2) => {
            console.error('[Cast] sendMessage falló en reintento — sesión posiblemente caída', e2);
          });
        }, 400);
      });
    }

    // Always relay via WS so LAN receivers (projector PCs, localhost:5174) stay
    // in sync even when a Chromecast session is active.
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(wsMessage));
      } catch { /* socket closed between readyState check and send */ }
    }
  },

  setQuickSlide: (qs) => {
    set({ quickSlide: qs });
    saveQuickSession(qs);
  },

  clearQuickSlide: () => {
    set({ quickSlide: null });
    saveQuickSession(null);
  },
  sendVideoControl: (action) => {
    // Multi-receiver coordination: stamp presentAt so all receivers (and the
    // server's master clock) flip state at the same wall-clock instant
    // despite varying RTT. Only meaningful for play/pause/stop/reset — mute
    // and seek don't need wall-clock alignment.
    const { wsReceiverCount, castActiveDevices } = get();
    const totalReceivers = wsReceiverCount + castActiveDevices.length;
    const needsCoord = totalReceivers >= 2
      && (action === 'play' || action === 'pause' || action === 'stop' || action === 'reset');
    const VIDEO_SYNC_AHEAD_MS = 150;
    const msg: CastMessage = { type: 'VIDEO_CONTROL', videoAction: action };
    const presentAt = needsCoord ? Date.now() + VIDEO_SYNC_AHEAD_MS : undefined;
    if (presentAt !== undefined) msg.presentAt = presentAt;
    get().sendToReceiver(msg);
    return presentAt;
  },
  requestVideoSync: () => {
    // clientNow lets the server echo it back so we can measure RTT and
    // compensate the returned seek target without needing a synced clock.
    get().sendToReceiver({ type: 'VIDEO_REQUEST_SYNC', clientNow: Date.now() });
  },
}));

// Broadcast SESSION_INFO when Chromecast active devices change
useCastStore.subscribe((state, prev) => {
  if (state.castActiveDevices === prev.castActiveDevices) return;
  const { ws } = state;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: 'SESSION_INFO', castDevices: state.castActiveDevices }));
});

// Broadcast DEVICES_INFO when discovered devices change
useCastStore.subscribe((state, prev) => {
  if (state.castDiscoveredDevices === prev.castDiscoveredDevices) return;
  const { ws } = state;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  ws.send(JSON.stringify({ type: 'DEVICES_INFO', devices: state.castDiscoveredDevices.map((d) => d.name) }));
});
